import { useEffect, useMemo, useState } from 'react';
import { Edit, Plus, Tag, Trash2, X } from 'lucide-react';
import type { Coupon } from '../../App';
import { offerAPI } from '../../lib/api';
import { OfferEditor } from './OfferEditor';

interface OfferMasterProps {
  restaurantId: string;
}

const parseJsonIdList = (raw: any): string[] => {
  if (raw === null || raw === undefined || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v)).filter(Boolean);
  const text = String(raw).trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map((v) => String(v)).filter(Boolean);
  } catch {
    // ignore
  }
  return [text];
};

const normalizeApplicableMode = (raw: any): 'all' | 'categories' | 'items' => {
  const mode = String(raw ?? '').trim().toLowerCase();
  if (mode === 'categories' || mode === 'category' || mode === 'c') return 'categories';
  if (mode === 'items' || mode === 'item' || mode === 'i') return 'items';
  return 'all';
};

const mapApiOffer = (restaurantId: string, offer: any): Coupon => ({
  id: String(offer.id),
  restaurantId: String(offer.restaurant_id ?? offer.restaurantId ?? restaurantId),
  code: String(offer.code ?? ''),
  description: String(offer.description ?? ''),
  discountType: (offer.discount_type ?? offer.discountType) === 'flat' ? 'flat' : 'percentage',
  discountValue: Number(offer.discount_value ?? offer.discountValue ?? 0),
  minOrderValue: Number(offer.min_order_value ?? offer.minOrderValue ?? 0),
  maxDiscount:
    offer.max_discount !== null && offer.max_discount !== undefined
      ? Number(offer.max_discount)
      : offer.maxDiscount !== null && offer.maxDiscount !== undefined
        ? Number(offer.maxDiscount)
        : undefined,
  isActive: Boolean(offer.is_active ?? offer.isActive),
  validFrom: String(offer.valid_from ?? offer.validFrom ?? new Date().toISOString()),
  validUntil: String(offer.valid_until ?? offer.validUntil ?? new Date().toISOString()),
  usageLimit:
    offer.usage_limit !== null && offer.usage_limit !== undefined
      ? Number(offer.usage_limit)
      : offer.usageLimit !== null && offer.usageLimit !== undefined
        ? Number(offer.usageLimit)
        : undefined,
  usedCount: Number(offer.used_count ?? offer.usedCount ?? 0),
  applicableMode: normalizeApplicableMode(offer.applicable_mode ?? offer.applicableMode),
  selectedCategoryIds: parseJsonIdList(
    offer.applicable_category_ids_norm ?? offer.applicableCategoryIdsNorm ?? offer.applicable_category_ids ?? offer.applicableCategoryIds
  ),
  selectedItemIds: parseJsonIdList(
    offer.applicable_item_ids_norm ?? offer.applicableItemIdsNorm ?? offer.applicable_item_ids ?? offer.applicableItemIds
  ),
});

export function OfferMaster({ restaurantId }: OfferMasterProps) {
  const [offers, setOffers] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editingOffer = useMemo(() => {
    if (!editingId) return null;
    return offers.find((o) => o.id === editingId) || null;
  }, [offers, editingId]);

  const loadOffers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await offerAPI.getByRestaurant(restaurantId, true);
      setOffers((response.data || []).map((row: any) => mapApiOffer(restaurantId, row)));
    } catch (e) {
      setOffers([]);
      setError(e instanceof Error ? e.message : 'Failed to load offers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const closeEditor = () => {
    setEditorMode(null);
    setEditingId(null);
  };

  const removeOffer = async (id: string) => {
    if (!confirm('Are you sure you want to delete this offer?')) return;
    setError(null);
    try {
      await offerAPI.delete(id);
      setSuccessMessage('Offer deleted');
      await loadOffers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete offer');
    }
  };

  const toggleStatus = async (id: string) => {
    const target = offers.find((o) => o.id === id);
    if (!target) return;
    setError(null);
    try {
      await offerAPI.update(id, { isActive: !target.isActive });
      await loadOffers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update offer');
    }
  };

  if (editorMode) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-5"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeEditor();
        }}
      >
        <div
          className="relative w-full max-w-6xl overflow-hidden rounded-2xl bg-slate-50 shadow-2xl max-h-[92vh]"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={closeEditor}
            aria-label="Close"
            className="absolute right-4 top-4 z-50 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow hover:bg-white"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="max-h-[92vh] overflow-y-auto">
            <OfferEditor
              restaurantId={restaurantId}
              mode={editorMode}
              initialCoupon={editorMode === 'edit' ? editingOffer : null}
              onCancel={closeEditor}
              onSave={async (payload) => {
                setError(null);

                const applicableMode =
                  payload.meta.applicableMode === 'categories'
                    ? 'CATEGORIES'
                    : payload.meta.applicableMode === 'items'
                      ? 'ITEMS'
                      : 'ALL';

                try {
                  if (editorMode === 'edit' && editingId) {
                    await offerAPI.update(editingId, {
                      title: payload.title,
                      description: payload.description,
                      discountType: payload.discountType,
                      discountValue: payload.discountValue,
                      minOrderValue: payload.minOrderValue,
                      maxDiscount: payload.maxDiscount,
                      isActive: payload.isActive,
                      validFrom: payload.validFrom,
                      validUntil: payload.validUntil,
                      usageLimit: payload.usageLimit,
                      applicableMode,
                      selectedCategoryIds: payload.meta.selectedCategoryIds,
                      selectedItemIds: payload.meta.selectedItemIds,
                    });
                    setSuccessMessage('Offer updated');
                  } else {
                    await offerAPI.create({
                      restaurantId,
                      code: payload.code,
                      title: payload.title,
                      description: payload.description,
                      discountType: payload.discountType,
                      discountValue: payload.discountValue,
                      minOrderValue: payload.minOrderValue,
                      maxDiscount: payload.maxDiscount,
                      validFrom: payload.validFrom,
                      validUntil: payload.validUntil,
                      usageLimit: payload.usageLimit,
                      isActive: payload.isActive,
                      applicableMode,
                      selectedCategoryIds: payload.meta.selectedCategoryIds,
                      selectedItemIds: payload.meta.selectedItemIds,
                    });
                    setSuccessMessage('Offer created');
                  }

                  closeEditor();
                  await loadOffers();
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Failed to save offer');
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-gray-900">Offer Management</h1>
          <p className="text-gray-600 text-sm">Create and manage offers for your restaurant</p>
        </div>
        <button
          onClick={() => {
            setEditorMode('create');
            setEditingId(null);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Offer
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <p className="text-gray-600">Loading offers...</p>
        </div>
      )}

      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className={`bg-white rounded-xl shadow-sm border-2 p-6 ${
                offer.isActive ? 'border-blue-200' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-5 h-5 text-blue-600" />
                    <h3 className="text-gray-900">{offer.code}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 whitespace-pre-line">{offer.description}</p>
                </div>
                <button
                  onClick={() => toggleStatus(offer.id)}
                  className={`px-3 py-1 rounded-full text-xs ${
                    offer.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {offer.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>

              <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="text-gray-900">
                    {offer.discountType === 'percentage' ? `${offer.discountValue}%` : `₹${offer.discountValue}`}
                    {offer.maxDiscount && offer.discountType === 'percentage' && ` (max ₹${offer.maxDiscount})`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Min. Order:</span>
                  <span className="text-gray-900">₹{offer.minOrderValue}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Valid Until:</span>
                  <span className="text-gray-900">{new Date(offer.validUntil).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setEditorMode('edit');
                    setEditingId(offer.id);
                  }}
                  className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  aria-label="Edit"
                >
                  <Edit className="w-4 h-4 mx-auto" />
                </button>
                <button
                  onClick={() => removeOffer(offer.id)}
                  className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4 mx-auto" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && offers.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No offers yet. Create your first offer!</p>
        </div>
      )}
    </div>
  );
}
